import { CSSProperties, ReactNode, useEffect } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonVariants,
  Heading,
  HeadingProps,
  TextLink,
  Truncated,
  useTooltip,
} from '@frontend/design-system';
import { useChartContext } from '../chart.provider';
import { PDFDetails, useExportChartToPDF } from '../hooks';

export type ChartHeaderAction = {
  buttonVariant?: ButtonVariants;
  disabled?: boolean;
  label: string;
  omit?: boolean;
  onClick: () => void;
  trackingId?: string;
} & (
  | {
      hoverText?: never;
      iconName?: IconName;
      showLabel?: boolean;
    }
  | {
      hoverText?: string;
      iconName?: never;
      showLabel?: never;
    }
);

type HeaderProps = {
  actions?: ChartHeaderAction[];
  bottomElement?: ReactNode | null;
  infoTip?: ReactNode;
  leftElement?: ReactNode | null | false;
  rightElement?: ReactNode | null | false;
  removeHeaderBottomMargin?: boolean;
  headerLevel?: HeadingProps['level'];
  style?: CSSProperties;
  subtitle?: string | ReactNode | false;
  title: string;
  trackingIdHelper?: string;
} & (
  | {
      allowExportToPdf?: boolean;
      pdfDetails?: PDFDetails;
    }
  | {
      allowExportToPdf?: never;
      pdfDetails?: never;
    }
);

export const Header = ({
  actions = [],
  allowExportToPdf,
  bottomElement,
  infoTip,
  leftElement,
  rightElement,
  pdfDetails = [],
  removeHeaderBottomMargin,
  headerLevel = 3,
  subtitle,
  title,
  ...rest
}: HeaderProps) => {
  const { t } = useTranslation('analytics');
  const { chartContainer, isSmallMode, setShowLoader } = useChartContext();
  const { exportToPDF, isExportingPDF } = useExportChartToPDF(pdfDetails);
  const {
    Tooltip,
    tooltipProps,
    triggerProps: tooltipTriggerProps,
  } = useTooltip({ placement: 'top', trigger: 'hover' });

  if (allowExportToPdf) {
    actions.push({
      iconName: 'download',
      label: t('Export to PDF'),
      onClick: () => exportToPDF(title, chartContainer),
    });
  }

  useEffect(() => {
    setShowLoader(isExportingPDF);
  }, [isExportingPDF]);

  return (
    <div {...rest}>
      <header css={styles.header} style={bottomElement || removeHeaderBottomMargin ? { marginBottom: 0 } : {}}>
        <div className='title-wrapper'>
          {leftElement}
          <Heading className={`heading ${isSmallMode ? 'small-mode' : ''}`.trim()} level={headerLevel}>
            <Truncated as='span'>{title}</Truncated>
            {infoTip}
          </Heading>
          {rightElement}
        </div>
        {(actions.length || subtitle) && (
          <div className='actions-wrapper'>
            {subtitle &&
              (typeof subtitle === 'string' ? (
                <Truncated className='subtitle' color='light'>
                  {subtitle}
                </Truncated>
              ) : (
                subtitle
              ))}
            {actions.map(({ buttonVariant = 'secondary', hoverText, iconName, omit, onClick, showLabel, ...rest }) =>
              omit ? null : iconName ? (
                <Button
                  {...rest}
                  className='action-button'
                  iconName={iconName}
                  key={rest.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                  variant={buttonVariant}
                >
                  {showLabel ? <span className='action-label'>{rest.label}</span> : null}
                </Button>
              ) : (
                <div key={rest.label} {...tooltipTriggerProps}>
                  <TextLink
                    {...rest}
                    as='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      onClick();
                    }}
                    weight='bold'
                  >
                    {rest.label}
                  </TextLink>
                  {hoverText && <Tooltip {...tooltipProps}>{hoverText}</Tooltip>}
                </div>
              )
            )}
          </div>
        )}
      </header>
      {bottomElement}
    </div>
  );
};

const styles = {
  header: css`
    align-items: center;
    column-gap: ${theme.spacing(2)};
    display: grid;
    grid-template-columns: 1fr auto;
    margin-bottom: ${theme.spacing(2)};

    .title-wrapper {
      align-items: center;
      display: flex;
      min-width: 0;
      overflow: hidden;

      .heading {
        align-items: center;
        display: flex;
        flex-shrink: 1;
        gap: ${theme.spacing(0.5)};
        min-width: 0;

        &.small-mode {
          font-size: ${theme.font.size.large};
        }

        button {
          margin-top: ${theme.spacing(0.25)};
        }
      }
    }

    .actions-wrapper {
      align-items: center;
      display: flex;
      flex-wrap: nowrap;
      gap: ${theme.spacing(1.5)};
      max-width: 100px;
      white-space: nowrap;

      @container (min-width: ${breakpoints.xsmall.max}px) {
        max-width: 100%;
      }

      .subtitle {
        font-weight: ${theme.font.weight.bold};
        color: ${theme.font.colors.light};
      }

      .action-button {
        .action-label {
          display: none;

          @container (min-width: ${breakpoints.xsmall.max}px) {
            display: inline;
          }
        }

        svg {
          min-height: ${theme.spacing(2.5)};
          min-width: ${theme.spacing(2.5)};
        }
      }
    }
  `,
};
